import { useQueryClient } from "@tanstack/react-query";
import { MqttContext } from "controller/context/MqttCreateContext";
import { useGlobalStore } from "controller/store/globalStore";
import { useTimeEntryStore } from "controller";
import { useContext, useEffect } from "react";
import type { ActiveTimeEntry, TimerMqttEvent, TimerStartedEvent } from "shared";
import { debugConsole, getFullName } from "utilz/helpers";

// Build a store `ActiveTimeEntry` from a self-sufficient `started` payload
// (mirrors how `setActiveTimeEntries` composes the name from its raw item).
const mapStartedEventToTimeEntry = (
  data: TimerStartedEvent,
): ActiveTimeEntry => ({
  mapping_id: data.mapping_id,
  service_id: data.service_id,
  service_type: data.service_type,
  start_time: data.start_time,
  service_summary: data.service_summary ?? undefined,
  u_id: data.u_id,
  customer_id: data.customer_id ?? undefined,
  customer_user_id: data.customer_user_id ?? undefined,
  customer_name: data.customer_name ?? undefined,
  customer_user_name: getFullName(
    data.customer_user_first_name,
    data.customer_user_middle_name,
    data.customer_user_last_name,
  ),
  email: data.email ?? undefined,
  profile_image: data.profile_image ?? undefined,
  project_id: data.project_id ?? undefined,
});

/**
 * Subscribes to realtime timer start/stop events for the current resource via
 * MQTT (topic `service_desk/timer/{msp_id}/{resource_id}` — one topic per
 * resource, the timer owner, so the broker delivers only this user's events).
 *
 * `started` (Option B): the payload is self-sufficient, so we build an
 * `ActiveTimeEntry` and add it straight to `timeEntryStore` — no refetch (per
 * the "Never Invalidate Queries on MQTT" standard). The store ticks it locally
 * from `start_time`.
 *
 * `stopped`: the payload carries `service_type`, so the entry is removed from
 * `timeEntryStore` directly — no refetch either.
 *
 * Mounted once, globally (top bar). Every timer surface (ticket header,
 * watchlist, pinned chat) reads the same store, so they all update live.
 */
export const useTimeEntryMqtt = () => {
  const { mqttMessage, subscribeRoom, unSubscribeRoom, isConnected } =
    useContext(MqttContext);
  const queryClient = useQueryClient();
  const mspId = useGlobalStore((state) => state.mspId);
  const resourceId = useGlobalStore((state) => state.resourceId);
  const addTimeEntry = useTimeEntryStore((state) => state.addTimeEntry);
  const removeTimeEntry = useTimeEntryStore((state) => state.removeTimeEntry);

  const topic =
    mspId && resourceId ? `service_desk/timer/${mspId}/${resourceId}` : null;

  // Subscribe to the resource timer topic once the shared MQTT client is
  // connected (the connection is established globally, so we don't connect here).
  useEffect(() => {
    if (!isConnected || !topic) return;
    debugConsole("🚀 ~ useTimeEntryMqtt ~ subscribing to:", topic);
    subscribeRoom(topic);
    return () => {
      unSubscribeRoom(topic);
    };
  }, [isConnected, topic]);

  // Resync on (re)connect: events sent while disconnected are not replayed
  // (clean session), so refetch once to catch anything missed.
  useEffect(() => {
    if (!isConnected || !topic) return;
    queryClient.invalidateQueries(["timeEntries"]);
  }, [isConnected, topic]);

  // Handle incoming timer events.
  useEffect(() => {
    if (!mqttMessage || !topic || mqttMessage.topic !== topic) return;
    try {
      const data = JSON.parse(mqttMessage.message) as TimerMqttEvent;
      debugConsole(
        "🚀 ~ useTicketTimeEntryMqtt ~ timer event received:",
        mqttMessage.topic,
        data,
      );

      if (data.event === "started") {
        addTimeEntry(mapStartedEventToTimeEntry(data));
        return;
      }

      if (data.event === "stopped") {
        // `ticket_id` is the universal service id (same key for
        // ticket/project/task — only `service_type` differs), so matching on
        // serviceId + serviceType targets the right entry for every type.
        removeTimeEntry({
          serviceId: data.ticket_id,
          timeEntryId: data.time_entry_mapping_id,
          serviceType: data.service_type,
        });
      }
    } catch (error: unknown) {
      debugConsole(
        "🚀 ~ useTimeEntryMqtt ~ failed to parse timer event:",
        error,
      );
    }
  }, [mqttMessage, topic, addTimeEntry, removeTimeEntry]);
};
